/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Material UI table
import { MaterialReactTable } from "material-react-table";
//Material UI components for rendring menu and others
import { MenuItem, Box } from "@mui/material";

import BreadCrumb from "components/Common/BreadCrumb/BreadCrumb";
import ChallengesCommonHeader from "components/ChallengeComponents/Common/Header/ChallengesCommonHeader";

/*------ import url and method ------*/
import * as url from "helper/UrlHelper";
import { getData } from "utils/Gateway";

import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import { assetImages } from "constants";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import ChallengeTypeOptions from "data/Challenge/ChallengeType.json";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";

// Updated to accept a 'cardType' for styling
const DashboardCard = ({ title, items, cardType, subtitle }) => {
  const { t } = useTranslation();
  // Card color mapping and icon backgrounds
  const cardBg = {
    challenges: "rgb(130 128 255 / 20%)",
    responses: "rgb(40 167 69 / 20%)",
    pending: "rgb(254 197 61 / 20%)",
    accepted: "rgb(23 162 184 / 20%)",
    rejected: "rgb(220 53 69 / 20%)",
    process: "rgb(111 66 193 / 20%)",
  };
  const cardColor = {
    challenges: "#8280FF",
    responses: "#28a745",
    pending: "#FEC53D",
    accepted: "#17a2b8",
    rejected: "#dc3545",
    process: "#6f42c1",
  };
  // Main stat, secondary stat, percent change
  const totalCount = items[0]?.value ?? 0;
  const currentMonthCount = items[1]?.value ?? 0;
  const prevMonthCount = items[2]?.value ?? 0;
  const percentChange =
    items[1]?.value > 0 && items[2]?.value > 0
      ? (((currentMonthCount - prevMonthCount) / prevMonthCount) * 100).toFixed(
          2
        )
      : 0;
  return (
    <div className="col-lg-3 col-md-6 mb-4 mb-lg-0">
      <div className="project h-100 bg-gray-200 p-3 p-lg-4 d-flex gap-3 rounded-10 shadow-sm">
        <div className="info flex-fill">
          <p className="fw-semibold mb-3">{t(title)}</p>
          <div className="d-flex align-items-end gap-1 mb-1">
            <h2 className="fw-bold lh-1 mb-0">{currentMonthCount}</h2>
            <h3 className="text-gray fw-bold lh-1 mb-1">/{totalCount}</h3>
          </div>
          <p className="fs-xs d-flex align-items-center">
            <span
              className={`d-block material-symbols-outlined icon-md me-1 ${
                percentChange >= 0 ? "text-success" : "text-danger"
              }`}
            >
              {percentChange >= 0 ? "trending_up" : "trending_down"}
            </span>
            <span
              className={`d-block ${
                percentChange >= 0 ? "text-success" : "text-danger"
              }`}
            >
              {Math.abs(percentChange)}% from {prevMonthCount} {t(subtitle)}{" "}
              last month
            </span>
          </p>
        </div>
        <div
          className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-18"
          style={{
            width: "3.75rem",
            height: "3.75rem",
            backgroundColor: cardBg[cardType] || "#eee",
            color: cardColor[cardType] || "#333",
          }}
        >
          <span
            className="d-block material-symbols-outlined icon-fill"
            style={{ fontSize: "2rem" }}
          >
            {items[0]?.icon || "dashboard"}
          </span>
        </div>
      </div>
    </div>
  );
};

const ChallengeDashboardBody = () => {
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const moduleAccess = localStorage.getItem("moduleaccess");

  const { t } = useTranslation();
  const breadcrumbTxt = [{ title: t("Challenges") + ` / ` + t("Dashboard") }];

  const [challengeListLoading, setChallengeListLoading] = useState(false);
  const [challengeList, setChallengeList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [challengeDashboardData, setChallengeDashboardData] = useState({});

  //alert requirements
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  //function for get project list
  const getAllChallenges = async () => {
    setChallengeListLoading(true);

    try {
      let requestUrl = `${url.API_BASE_URL}${url.API_GET_ALL_CHALLENGE}?token=${token}&modulefrom=backend&start=0&limit=10`;

      console.log(requestUrl);

      const response = await getData(requestUrl);
      setChallengeListLoading(false);

      console.log(response);

      if (response.status) {
        setMessageType("success");
        setChallengeList(response.data);
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (error) {
      setMessageType("error");
      setAlertMessage(error.message);
    }
    setShowAlert(true);
  };

  //function for get dashboard data
  const getDashboardData = async () => {
    setIsLoading(true);
    try {
      let requestUrl = `${url.API_BASE_URL}${url.API_GET_CHALLENGE_DASHBOARD_DATA}?token=${token}&userrolestring=${userInfo.role.slug}`;

      const response = await getData(requestUrl);

      console.log("challenge dashboard data response >>>", response);
      if (response.status) {
        setChallengeDashboardData(response.data);
      } else {
        setMessageType("error");
        setAlertMessage(response.message || "Failed to fetch dashboard data.");
        setShowAlert(true);
      }
    } catch (error) {
      setMessageType("error");
      setAlertMessage(error.message || "An unexpected error occurred.");
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAllChallenges();
    getDashboardData();
  }, []);

  // columns for challenge list
  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: t("Title"),
        size: 250,
      },
      {
        accessorKey: "challengetype",
        header: t("Type"),
        Cell: ({ row }) => {
          const challengeTypeValue = row.original.challengetype;
          // Find matching label from ChallengeTypeOptions or default to "Talent Hunt"
          const typeOption = ChallengeTypeOptions.find(
            (type) => type.value === challengeTypeValue
          );
          const displayLabel = typeOption ? typeOption.label : "Talent Hunt";

          return <span>{displayLabel}</span>;
        },
      },
      {
        accessorKey: "moderatorname",
        header: t("Posted By"),
        size: 200,
        Cell: ({ row }) => (
          <div className="d-flex border-bottom-0">
            <div className="d-flex align-items-center gap-2">
              <div
                className="avatar rounded-circle overflow-hidden"
                style={{ width: "25px", height: "25px" }}
              >
                <img
                  src={
                    row.original.moderatorimage == ""
                      ? assetImages.defaultUser
                      : url.SERVER_URL + row.original.moderatorimage
                  }
                  alt="admin"
                  className="w-100 h-100 object-fit-cover object-center"
                />
              </div>
              <p>{row.original.moderatorname}</p>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "postededdate",
        header: t("Posted on"),
      },
      {
        accessorKey: "jobcode",
        header: t("Code"),
      },
      {
        accessorKey: "jobtype",
        header: t("Job Type"),
      },
      {
        accessorKey: "companynamestring",
        header: t("Company"),
      },
      {
        accessorKey: "minsalary",
        header: t("Min Salary"),
      },
      {
        accessorKey: "maxsalary",
        header: t("Max Salary"),
      },
      {
        accessorKey: "paymentduration",
        header: t("Payment Type"),
      },
      {
        accessorKey: "remotejob",
        header: t("Remote Job"),
      },
      {
        accessorKey: "city",
        header: t("City"),
      },
      {
        accessorKey: "requiredskillstring",
        header: t("Required Skills"),
      },
      {
        accessorKey: "optionalskillstring",
        header: t("Optional Skills"),
      },
    ],
    []
  );

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  // Data for All Dashboard Cards
  const challengeOverviewDetails = [
    {
      label: "Total",
      value: challengeDashboardData?.totalchallengecount,
      icon: "emoji_events",
    },
    {
      label: "Current Month",
      value: challengeDashboardData?.currentmonthchallengecount,
      icon: "event",
    },
    {
      label: "Previous Month",
      value: challengeDashboardData?.previousmonthchallengecount,
      icon: "event_repeat",
    },
  ];

  const challengeResponseDetails = [
    {
      label: "Total",
      value: challengeDashboardData?.totalchallengeresponsecount,
      icon: "forum",
    },
    {
      label: "Current Month",
      value: challengeDashboardData?.currentmonthchallengeresponsecount,
      icon: "event",
    },
    {
      label: "Previous Month",
      value: challengeDashboardData?.previouschallengeresponsecount,
      icon: "event_repeat",
    },
  ];

  const pendingResponseDetails = [
    {
      label: "Total",
      value: challengeDashboardData?.totalpendingchallengeresponsecount,
      icon: "pending_actions",
    },
    {
      label: "Current Month",
      value: challengeDashboardData?.pendingcurrentchallengeresponsecount,
      icon: "event",
    },
    {
      label: "Previous Month",
      value: challengeDashboardData?.pendingprevchallengeresponsecount,
      icon: "event_repeat",
    },
  ];

  const acceptedResponseDetails = [
    {
      label: "Total",
      value: challengeDashboardData?.totalacceptedchallengeresponsecount,
      icon: "check_circle",
    },
    {
      label: "Current Month",
      value: challengeDashboardData?.currentacceptedchallengeresponsecount,
      icon: "event",
    },
    {
      label: "Previous Month",
      value: challengeDashboardData?.previousacceptedchallengeresponsecount,
      icon: "event_repeat",
    },
  ];

  const rejectedResponseDetails = [
    {
      label: "Total",
      value: challengeDashboardData?.totalrejectedchallengeresponsecount,
      icon: "cancel",
    },
    {
      label: "Current Month",
      value: challengeDashboardData?.currentrejectedchallengeresponsecount,
      icon: "event",
    },
    {
      label: "Previous Month",
      value: challengeDashboardData?.prevreectedchallengeresponsecount,
      icon: "event_repeat",
    },
  ];

  const challengeProcessDetails = [
    {
      label: "Total",
      value: challengeDashboardData?.totalchallengeprocesscount,
      icon: "settings_suggest",
    },
  ];

  if (
    !userInfo ||
    !(
      userInfo.role.slug === "ADMIN" ||
      userInfo.role.slug === "SUPER_ADMIN" ||
      moduleAccess.includes("MOD_CHALLENGE")
    )
  ) {
    return <AccessDeniedView />;
  }

  return (
    <section className="projects-wrapper bg-white pb-5">
      <ChallengesCommonHeader componentName="dashboard" />

      <div className="container-fluid px-lg-5">
        <BreadCrumb breadCrumbText={breadcrumbTxt} bottom={true} />

        <div className="p-3 p-sm-4 shadow-md rounded-10 mb-4">
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              <div className="row mb-4">
                <DashboardCard
                  title="Total Jobs"
                  items={challengeOverviewDetails}
                  cardType="challenges"
                  subtitle="jobs"
                />
                <DashboardCard
                  title="Total Responses"
                  items={challengeResponseDetails}
                  cardType="responses"
                  subtitle="responses"
                />
                <DashboardCard
                  title="Pending Responses"
                  items={pendingResponseDetails}
                  cardType="pending"
                  subtitle="responses"
                />
                <DashboardCard
                  title="Accepted Responses"
                  items={acceptedResponseDetails}
                  cardType="accepted"
                  subtitle="responses"
                />
              </div>
              <div className="row mb-4">
                <DashboardCard
                  title="Rejected Responses"
                  items={rejectedResponseDetails}
                  cardType="rejected"
                  subtitle="responses"
                />
                <DashboardCard
                  title="Total Process"
                  items={challengeProcessDetails}
                  cardType="process"
                  subtitle="processes"
                />
              </div>
            </>
          )}
        </div>

        <div className="row mb-4">
          <div className="col-12">
            <div className="p-3 p-sm-4 shadow-md rounded-10">
              <div className="d-flex align-items-center justify-content-between mb-4">
                <h3 className="mb-0">Latest Challenges</h3>
              </div>
              {challengeListLoading ? (
                <LoadingSkeleton />
              ) : (
                <div className="table-wrapper">
                  <MaterialReactTable
                    columns={columns} // map columns to be displayed with api data,
                    data={challengeList} // data from api to be displayed
                    enableGrouping
                    // enableRowSelection // enable showing checkbox
                    getRowId={(row) => row._id} // map which value to select with row checkbox
                    // onRowSelectionChange={setRowSelection} //connect internal row selection state to your own
                    // state={{ rowSelection }} //pass our managed row selection state to the table to use
                    enableRowActions // show the action column
                    renderRowActionMenuItems={({ row }) => [
                      <MenuItem key="details">
                        <Link to={`/admin/challenges/jobinformation/${row.id}`}>
                          {t("View Details")}
                        </Link>
                      </MenuItem>,
                      <MenuItem key="process">
                        <Link to={`/admin/challenges/hiringprocess/${row.id}`}>
                          {t("View Hiring Process")}
                        </Link>
                      </MenuItem>,
                      <MenuItem key="team">
                        <Link to={`/admin/challenges/hiringteam/${row.id}`}>
                          {t("View Hiring Team")}
                        </Link>
                      </MenuItem>,
                      <MenuItem key="response">
                        <Link to={`/admin/challenges/responses/list/${row.id}`}>
                          {t("View Responses")}
                        </Link>
                      </MenuItem>,
                      // <MenuItem key="survey">
                      //   <Link to={`/admin/challenges/survey/${row.id}`}>
                      //     {t("View Survey")}
                      //   </Link>
                      // </MenuItem>,
                    ]} // action columns menu items
                    muiTableContainerProps={{
                      sx: {
                        maxHeight: "60vh",
                      },
                    }}
                    enableStickyHeader
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {showAlert && (
          <AlertNotification
            showAlert={showAlert}
            message={alertMessage}
            alertType={messageType}
            onClose={onAlertClose}
          />
        )}
      </div>
    </section>
  );
};

export default ChallengeDashboardBody;
